import { apiService } from './api';
import type { GamePricing, RevenueData, Sale, ApiResponse } from '../types/api';

/**
 * Revenue Service
 * Handles game pricing and revenue operations for creators
 */
export class RevenueService {
  
  /**
   * Get pricing for a game
   * @param gameId - Game UUID
   * @returns Current pricing information
   */
  async getGamePricing(gameId: string): Promise<GamePricing | null> {
    console.log(`💰 Getting pricing for game ${gameId}`);
    
    try {
      const response = await apiService.get<ApiResponse<GamePricing>>(
        `/creator/games/${gameId}/pricing`
      );
      
      if (response.data && response.data.success && response.data.data) {
        console.log('✅ Pricing retrieved:', response.data.data);
        return response.data.data;
      }
      
      return null;
    } catch (error: any) {
      console.error('❌ Failed to get pricing:', error);
      // Return null if no pricing is set (404) or other error
      return null;
    }
  }

  /**
   * Set pricing for a game
   * @param gameId - Game UUID
   * @param isPaid - Whether the game is paid
   * @param price - Price in dollars (e.g., 4.99)
   * @returns Updated pricing information
   */
  async setGamePricing(gameId: string, isPaid: boolean, price: number): Promise<GamePricing> {
    console.log(`💰 Setting pricing for game ${gameId}:`, { isPaid, price });
    
    try {
      const response = await apiService.put<ApiResponse<GamePricing>>(
        `/creator/games/${gameId}/pricing`,
        {
          is_paid: isPaid,
          price: price,
        }
      );
      
      if (response.data && response.data.success && response.data.data) {
        console.log('✅ Pricing updated successfully:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data?.message || 'Failed to update pricing');
    } catch (error: any) {
      console.error('❌ Failed to set pricing:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to update pricing');
    }
  }
  
  /**
   * Get total revenue for the authenticated creator
   * Includes revenue across all games owned by the creator
   * @returns Total revenue data with per-game breakdown
   */
  async getTotalRevenue(): Promise<RevenueData> {
    console.log('💰 Fetching total revenue for creator');
    
    try {
      const response = await apiService.get<ApiResponse<RevenueData>>('/creator/revenue');
      
      if (response.data && response.data.success && response.data.data) {
        console.log('✅ Revenue data retrieved:', response.data.data);
        return response.data.data;
      }
      
      // Return empty data if no revenue yet
      return {
        total_sales: 0,
        total_revenue: 0,
        platform_fee: 0,
        creator_earnings: 0,
        currency: 'USD',
        games: [],
      };
    } catch (error: any) {
      console.error('❌ Failed to fetch total revenue:', error);
      
      // Return empty data on error (e.g., if user has no games yet)
      if (error.response?.status === 404) {
        return {
          total_sales: 0,
          total_revenue: 0,
          platform_fee: 0,
          creator_earnings: 0,
          currency: 'USD',
          games: [],
        };
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch revenue');
    }
  }
  
  /**
   * Get revenue for a specific game
   * @param gameId - Game UUID
   * @returns Revenue data for the specific game
   */
  async getGameRevenue(gameId: string): Promise<RevenueData> {
    console.log(`💰 Fetching revenue for game ${gameId}`);
    
    try {
      const response = await apiService.get<ApiResponse<RevenueData>>(
        `/creator/revenue/${gameId}`
      );
      
      if (response.data && response.data.success && response.data.data) {
        console.log('✅ Game revenue data retrieved:', response.data.data);
        return response.data.data;
      }
      
      // Return empty data if no revenue yet
      return {
        total_sales: 0,
        total_revenue: 0,
        platform_fee: 0,
        creator_earnings: 0,
        currency: 'USD',
        games: [],
      };
    } catch (error: any) {
      console.error('❌ Failed to fetch game revenue:', error);
      
      // Return empty data on error
      if (error.response?.status === 404) {
        return {
          total_sales: 0,
          total_revenue: 0,
          platform_fee: 0,
          creator_earnings: 0,
          currency: 'USD',
          games: [],
        };
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch game revenue');
    }
  }

  /**
   * Get all individual sales for the authenticated creator
   * @returns List of all sales
   */
  async getSales(): Promise<Sale[]> {
    console.log('💰 Fetching sales for creator');
    
    try {
      const response = await apiService.get<ApiResponse<Sale[]>>('/creator/sales');
      
      if (response.data && response.data.success && response.data.data) {
        console.log('✅ Sales data retrieved:', response.data.data);
        return response.data.data;
      }
      
      return [];
    } catch (error: any) {
      console.error('❌ Failed to fetch sales:', error);
      
      // Return empty array on error
      if (error.response?.status === 404) {
        return [];
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch sales');
    }
  }
}

// Export singleton instance
export const revenueService = new RevenueService();
export default revenueService;
